import fr from "../../static/assets/fr.png";
import en from "../../static/assets/en.png";
import es from "../../static/assets/es.png";
import se from "../../static/assets/se.svg";
import de from "../../static/assets/de.svg";
import { getAzureBlob } from "../../services/container";
import moment from "moment";

export const getMostRecentHistory = (history) => {
  if (!history || history.length === 0) {
    return null;
  }
  let mostRecent = history[0];
  for (let i = 1; i < history.length; i++) {
    const currentTime = new Date(history[i]._time);
    const mostRecentTime = new Date(mostRecent._time);

    if (currentTime > mostRecentTime) {
      mostRecent = history[i];
    }
  }
  return mostRecent;
};

export const getCameraId = (url) => {
  const startIndex = url.indexOf("camera-ak_");
  const endIndex = url.indexOf("/", startIndex);
  return url.substring(startIndex + "camera-ak_".length, endIndex);
};

/**
 * Phone / mobile-app captures use camera folders `camera-ak_mobile_<corpId>/…`.
 * Characterization is not offered for those images in Historique.
 */
export const isPhoneCaptureImageUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  return url.includes("camera-ak_mobile_") || url.includes("/ak_mobile_");
};

/** Same fallback as AppSidebar / Settings when corporationModules is missing. */
export const isCharacterizationModuleOn = () => {
  try {
    const modules = JSON.parse(
      localStorage.getItem("corporationModules") || "null",
    );
    return modules ? Boolean(modules.showCharacterizationModule) : true;
  } catch {
    return true;
  }
};

/** Historique "To characterize": needs the module and a non-phone capture. */
export const canCharacterizeFromHistorique = (imageUrl) =>
  isCharacterizationModuleOn() && !isPhoneCaptureImageUrl(imageUrl);

/**
 * i18n key explaining why "To characterize" is disabled in Historique, or null if allowed.
 */
export const getCharacterizeFromHistoriqueBlockReasonKey = (imageUrl) => {
  if (!isCharacterizationModuleOn()) return "toCharacterizeModuleOff";
  if (isPhoneCaptureImageUrl(imageUrl)) return "toCharacterizePhoneBlocked";
  return null;
};

export const languageData = [
  { label: "Deutsch", value: "de", flag: de },
  { label: "English", value: "en", flag: en },
  { label: "Español", value: "es", flag: es },
  { label: "Français", value: "fr", flag: fr },
  { label: "Svenska", value: "se", flag: se },
];

export const tagsOptions = [
  { key: "downgrading", value: "downgrading" },
  { key: "overload", value: "overload" },
  { key: "emptyRun", value: "empty run" },
  { key: "billedWithoutService", value: "absence of billing" },
];

export const repDescriptions = {
  pmcb_mineral: {
    label: "pmcb_mineral",
    category: "PMCB Category 1",
    descriptions:
      "Building products and materials predominantly composed of minerals (Concrete, Bricks, etc.)",
  },
  pmcb: {
    label: "pmcb",
    category: "PMCB Category 2",
    descriptions: "Other building products and materials (Metal, Wood, etc.)",
  },
};

export const reduceImageQuality = (imageUrl, quality = 0.5) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const compressedDataUrl = canvas.toDataURL("image/jpeg", quality);

      resolve(compressedDataUrl);
    };

    img.onerror = reject;
    img.src = imageUrl;
  });
};

export function formatPercentage(value, total) {
  if (total === 0) return "0";
  const percentage = (value / total) * 100;

  if (percentage === 0) return "0";
  if (percentage < 0.01) return percentage.toFixed(4);
  if (percentage < 1) return percentage.toFixed(2);
  return percentage.toFixed(1);
}

export const STATUS_OPTIONS = (t) => [
  {
    value: "done",
    label: t("Verified"),
    icon: "fas fa-check-circle",
    color: "text-green-600",
    statusIcon: "validated",
    pillBackground: "#E8F8F0",
    pillTextColor: "#1F7A55",
  },
  {
    value: "warning",
    label: t("Warning"),
    icon: "fas fa-exclamation-circle",
    color: "text-amber-500",
    statusIcon: "warning",
    pillBackground: "#FFF4E5",
    pillTextColor: "#B45309",
  },
  {
    value: "interrogation",
    label: t("Interrogation"),
    icon: "fas fa-question-circle",
    color: "text-red-500",
    statusIcon: "interrogation",
    pillBackground: "#FFF0F0",
    pillTextColor: "#C24141",
  },
  {
    value: "none",
    label: t("No status"),
    icon: "fas fa-minus-circle",
    color: "text-gray-400",
    statusIcon: "toProcess",
    pillBackground: "#F3F4F6",
    pillTextColor: "#6B7280",
  },
];
export const hasOpeningHours = (opening) => {
  return Object.values(opening).some((day) =>
    day.some(([start, end]) => start.trim() !== "" && end.trim() !== ""),
  );
};

/** Blob name based on the capture time, used by revision history. */
export const getOutputSurfaceBlobName = (image_url, time, measurement) => {
  const cameraId = getCameraId(image_url);
  return `camera-ak_${cameraId}/output-surface/${time
    .replace(/:/g, "_")
    .slice(0, -1)}-camera-ak_${cameraId}-${measurement}-surface.json`;
};

/** Blob name matching the image-based output-surface read in ImageTreatment. */
export const getOutputSurfaceBlobNameFromImageUrl = (
  image_url,
  measurement,
) => {
  if (!image_url || !measurement) return null;

  try {
    const imagePath = new URL(image_url).pathname.replace(
      /^\/(?:camera-analysis\/)?/,
      "",
    );
    if (!imagePath.includes("/pictures/")) return null;

    return imagePath
      .replace("/pictures/", "/output-surface/")
      .replace(/(?:-R|-L)?\.jpeg$/, `-${measurement}-surface.json`);
  } catch {
    return null;
  }
};

/**
 * Legacy UTC blob first, canonical image-based blob last.
 * This keeps existing reviews readable while canonical data wins on conflicts.
 */
export const getOutputSurfaceBlobNames = (image_url, time, measurement) => {
  const legacyBlobName = time
    ? getOutputSurfaceBlobName(image_url, time, measurement)
    : null;
  const canonicalBlobName = getOutputSurfaceBlobNameFromImageUrl(
    image_url,
    measurement,
  );

  return [...new Set([legacyBlobName, canonicalBlobName].filter(Boolean))];
};

const hasRevisionMetadata = (segmentation) =>
  segmentation?.reviewCls?.trim() ||
  segmentation?.comment?.trim() ||
  segmentation?.reviewStatus ||
  segmentation?.suggestedForDeletion ||
  segmentation?.deleteSuggestionComment?.trim() ||
  segmentation?.restoredAt ||
  (segmentation?.oldCls && segmentation.oldCls !== segmentation.cls);

const mergeRevisionEntries = (entries) => {
  const mergedById = new Map();

  entries.forEach((entry, index) => {
    const key = entry?.id ?? `entry-${index}`;
    const existing = mergedById.get(key);
    if (!existing) {
      mergedById.set(key, entry);
      return;
    }

    const existingTime = Date.parse(existing.reviewTime || "") || 0;
    const nextTime = Date.parse(entry.reviewTime || "") || 0;
    mergedById.set(
      key,
      nextTime >= existingTime
        ? { ...existing, ...entry }
        : { ...entry, ...existing },
    );
  });

  return [...mergedById.values()];
};

export const getImageComments = async (
  azureUrl,
  measurement,
  image_url,
  time,
) => {
  const blobNames = getOutputSurfaceBlobNames(image_url, time, measurement);
  const commentsByBlob = await Promise.all(
    blobNames.map(async (blobName) => {
      try {
        const { data: outputSurface } = await getAzureBlob({
          blobUrl: azureUrl + blobName,
        });
        return (outputSurface?.object_segmentations || [])
          .filter(hasRevisionMetadata)
          .map((obj) => ({
            ...obj,
            image_url,
            sourceBlobName: blobName,
          }));
      } catch {
        return [];
      }
    }),
  );

  return mergeRevisionEntries(commentsByBlob.flat());
};

export const parseCameraImageUrl = (url) => {
  if (!url) return { cameraId: null, imageName: null };

  try {
    const parts = url.split("/");
    const cameraIndex = parts.indexOf("camera-analysis");

    if (cameraIndex === -1 || !parts[cameraIndex + 1]) {
      return { cameraId: null, imageName: null };
    }

    const cameraId = parts[cameraIndex + 1];
    let fileName = parts.pop();

    const imageName = fileName.split(".jpeg")[0];

    return { cameraId, imageName };
  } catch (error) {
    console.error("Erreur lors du parsing de l'URL:", error);
    return { cameraId: null, imageName: null };
  }
};

export const downloadFile = async (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const tempLink = document.createElement("a");
  tempLink.href = url;
  tempLink.setAttribute("download", fileName);
  document.body.appendChild(tempLink);
  tempLink.click();
  document.body.removeChild(tempLink);
  window.URL.revokeObjectURL(url);
};

export const sortByImageUrl = (a, b) => {
  // Extract the suffix -L and -R
  const regex = /-(L|R)\./;
  const suffixA = a.image_url?.match(regex)?.[1];
  const suffixB = b.image_url?.match(regex)?.[1];

  // Priority : -L first, then -R
  if (suffixA === "L" && suffixB !== "L") {
    return -1;
  } else if (suffixA !== "L" && suffixB === "L") {
    return 1;
  } else if (suffixA === "R" && suffixB !== "R") {
    return -1;
  } else if (suffixA !== "R" && suffixB === "R") {
    return 1;
  } else {
    return 0;
  }
};

export const displayDates = (
  container,
  idx,
  start,
  stop,
  clientName,
  clientAddress,
  i18n,
) => {
  let text =
    `${i18n.language === "fr" ? "Carac" : "Charac"}. ` +
    (idx + 1) +
    " : " +
    moment(start).format("DD/MM/YY, HH:mm");
  if (start !== stop) {
    text += " au " + moment(stop).format("DD/MM/YYYY, HH:mm");
  }
  if (!container.fictiveContainer?.active) {
    text +=
      " (" +
      (clientName !== "" ? clientName : "-") +
      (clientAddress && !["", " ", "-", "undefined"].includes(clientAddress)
        ? ", " + clientAddress
        : "") +
      ")";
  }
  return text;
};

/**
 * Extracts the reliablePrediction value from a container or enrichment object.
 * Handles both array and non-array formats, and checks both enrichment and direct properties.
 *
 * @param {Object} container - Container object that may have reliablePrediction
 * @param {Object} enrichment - Optional enrichment object with reliablePrediction
 * @returns {boolean|undefined} - The reliablePrediction value or undefined
 */
export const extractReliablePrediction = (container, enrichment = null) => {
  if (enrichment?.reliablePrediction !== undefined) {
    return Array.isArray(enrichment.reliablePrediction)
      ? enrichment.reliablePrediction[0]
      : enrichment.reliablePrediction;
  }
  if (container?.reliablePrediction !== undefined) {
    return Array.isArray(container.reliablePrediction)
      ? container.reliablePrediction[0]
      : container.reliablePrediction;
  }
  return undefined;
};

/**
 * Calculates the number of days between a fromDate and the current date.
 * Normalizes dates to UTC to avoid timezone issues.
 * Handles edge cases: invalid dates, future dates, null/undefined values.
 *
 * @param {string|Date|null|undefined} fromDate - The start date (from backend)
 * @param {Date} [currentDate] - Optional current date (defaults to now)
 * @returns {number|null} - Number of days (positive integer) or null if invalid
 */
export const calculateHistoryDays = (fromDate, currentDate = new Date()) => {
  // Handle null/undefined/empty values
  if (!fromDate) {
    return null;
  }

  // Parse the date
  const fromDateObj = new Date(fromDate);

  // Check if date is valid
  if (isNaN(fromDateObj.getTime())) {
    return null;
  }

  // Normalize both dates to UTC to avoid timezone issues
  // Get UTC timestamps at midnight for accurate day calculation
  const fromDateUTC = new Date(
    Date.UTC(
      fromDateObj.getUTCFullYear(),
      fromDateObj.getUTCMonth(),
      fromDateObj.getUTCDate(),
    ),
  );

  const currentDateUTC = new Date(
    Date.UTC(
      currentDate.getUTCFullYear(),
      currentDate.getUTCMonth(),
      currentDate.getUTCDate(),
    ),
  );

  // Calculate difference in milliseconds, then convert to days
  const diffMs = currentDateUTC.getTime() - fromDateUTC.getTime();
  const historyDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Return null for future dates (negative days) or invalid calculations
  if (historyDays < 0) {
    return null;
  }

  return historyDays;
};
